import React from 'react';
import { Icon } from 'antd';
import * as PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { inspectionShape } from '../utility/inspection-util';


/**
 * A summary card for display details about the inspection.
 * @param {string} props.titleKey The translation id for the title.
 * @param {string} props.iconType The ant icon type for the card.
 * @param {string} props.iconBackgroundColor The background colour for the icon.}
 */
function DetailsCard({
  titleKey, iconType, iconBackgroundColor, children,
}) {
  return (
    <div className="inspection-card details-card">
      <div className="details-icon" style={{ backgroundColor: iconBackgroundColor }}>
        <div className="details-icon-sizing">
          <Icon type={iconType} style={{ fontSize: '50px' }} />
        </div>
      </div>
      <div className="details-main">
        <h2 className="details-title">{titleKey}</h2>
        <div className="details-datas">
          {children}
        </div>
      </div>
    </div>
  );
}

DetailsCard.propTypes = {
  titleKey: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]).isRequired,
  iconType: PropTypes.string.isRequired,
  iconBackgroundColor: PropTypes.string.isRequired,
  children: PropTypes.any, // eslint-disable-line react/forbid-prop-types
};

DetailsCard.defaultProps = {
  children: undefined,
};


/**
 * An label + value item displayed on a summary card.
 * @param {string} props.label The label to display on the details card.
 * @param {string} props.value The value to display on the details card.
 */
const DetailData = ({ label, value }) => (
  <span className="details-data">
    <span className="details-label">{label}</span>
    <span className="details-value">{value}</span>
  </span>
);

DetailData.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
};


/**
 * Displays details about the inspection.
 * @param {object} inspection The inspection being displayed.
 */
function InspectionDetails({ inspection }) {
  let signatoryTranslKey;
  switch (inspection.signatoryType) {
    case 'INSPECTOR':
      signatoryTranslKey = 'signedByInspector';
      break;
    case 'CUSTOMER':
      signatoryTranslKey = 'signedByCustomer';
      break;
    case 'OTHER':
    case 'UNKNOWN':
    default:
      signatoryTranslKey = 'signedBy';
  }

  return (
    <DetailsCard titleKey={<FormattedMessage id="inspection" />} iconType="solution" iconBackgroundColor="rgb(207, 234, 250)">
      <DetailData label={<FormattedMessage id="inspector" />} value={inspection.createdBy ? inspection.createdBy.displayName : <FormattedMessage id="noData" />} />
      <DetailData label={<FormattedMessage id="location" />} value={inspection.locationDetails ? inspection.locationDetails.locationDescription : <FormattedMessage id="noData" />} />
      <DetailData label={<FormattedMessage id="reason" />} value={inspection.reason ? inspection.reason.reasonDescription : <FormattedMessage id="noData" />} />
      <DetailData/>
      <DetailData label={<FormattedMessage id={signatoryTranslKey} />} value={inspection.printedName || <FormattedMessage id="noData" />} />
      {inspection.customerEmail && <DetailData label={<FormattedMessage id="recipient" />} value={inspection.customerEmail || <FormattedMessage id="noData" />} />}
    </DetailsCard>
  );
}

InspectionDetails.propTypes = {
  inspection: inspectionShape.isRequired,
};


/**
 * Displays the fuel gauge as a meter.
 * @param {object} props The props of the react component.
 * @param {number} props.fuelTankLevel The level of the fuel tank. Must be in the range [0, 8]
 */
function FuelGauge({ fuelTankLevel }) {
  if (!fuelTankLevel) return <FormattedMessage id="noData" />;

  return (
    <div className="fuel-tank" title={`${fuelTankLevel} / 8`}>
      {
        [...Array(8).keys()].map(i => (
          <div key={Math.random()} className={`fuel-tank-block ${i < fuelTankLevel && 'fuel-tank-block--exists'} ${i % 2 === 0 && 'fuel-tank-block--main'}`} />
        ))
      }
    </div>
  );
}

FuelGauge.propTypes = {
  fuelTankLevel: PropTypes.number,
};
FuelGauge.defaultProps = {
  fuelTankLevel: null,
};


/**
 * Displays details about the vehicle.
 * @param {object} inspection The inspection being displayed.
 */
function VehicleDetails({ inspection }) {
  const { vehicle } = inspection;

  return (
    <DetailsCard titleKey={<FormattedMessage id="vehicle" />} iconType="car" iconBackgroundColor="rgb(243, 198, 208)">
      <DetailData label={<FormattedMessage id="regNumber" />} value={vehicle.regNumber} />
      <DetailData label={<FormattedMessage id="description" />} value={vehicle.vehicleDescription} />
      <DetailData label={<FormattedMessage id="odometer" />} value={`${inspection.odometerReading}`} />
      <DetailData label={<FormattedMessage id="fuelTank" />} value={<FuelGauge fuelTankLevel={inspection.fuelTankLevel} />} />
      <DetailData label={<FormattedMessage id="bayNumber" />} value={inspection.bayNumber || <FormattedMessage id="noData" />} />
    </DetailsCard>
  );
}

VehicleDetails.propTypes = {
  inspection: inspectionShape.isRequired,
};


/**
 * A display of the summary details about the inspection.
 * This is mostly around inspection meta data and vehicle data.
 * @param {object} props.inspection The inspection being displayed.
 */
export default function InspectionSummary({ inspection }) {
  return (
    <div className="via-centre details-section-wrapper">
      <div className="details-section">
        <InspectionDetails inspection={inspection} />
        <VehicleDetails inspection={inspection} />
      </div>
    </div>
  );
}

InspectionSummary.propTypes = {
  inspection: inspectionShape.isRequired,
};
